import debounce from "awesome-debounce-promise";
import { clsx } from "clsx";
import { Component, type ReactElement } from "react";
import type { FormatOptionLabelMeta } from "react-select";

import { HStack } from "widgets";

import ReactSelectField, { ReactSelectReduxFormField } from "./redux-form/ReactSelectField.js";
import { type ISelectOption } from "@/app-utils/form.ts";
import { type SearchScope, type Template } from "@/graphql.ts";
import { ENTITY_TYPE, SEARCH_RESTRICTION_TYPES } from "@/settings/settings.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";

export const restrictionTypeOptions: Record<string, ISelectOption> = {
  [SEARCH_RESTRICTION_TYPES.GENOME]: { label: "Genome", value: SEARCH_RESTRICTION_TYPES.GENOME },
  [SEARCH_RESTRICTION_TYPES.SPECIES]: { label: "Species", value: SEARCH_RESTRICTION_TYPES.SPECIES },
  [SEARCH_RESTRICTION_TYPES.DATABASE]: {
    label: "Database",
    value: SEARCH_RESTRICTION_TYPES.DATABASE,
  },
};

export interface ISearchScopeSelectorProps {
  searchType: string;
  restrictionType: ISelectOption;
  restrictionTypeFieldKey: string;
  scopeFieldKey: string;
  loadScopeOptions: (restrictionType: string, search: string) => Promise<SearchScope[]>;
  change: (field: string, value: string) => void;
}

interface ISearchScopeSelectorState {
  searchRestrictionFieldKey: number;
}

class SearchScopeSelector extends Component<ISearchScopeSelectorProps, ISearchScopeSelectorState> {
  static DEBOUNCE_TIMER = 300;

  private searchScopeOptionsCache = {};

  constructor(props) {
    super(props);

    this.state = { searchRestrictionFieldKey: 0 };

    this.onChangeRestrictionType = this.onChangeRestrictionType.bind(this);
    this.loadOptions = this.loadOptions.bind(this);

    // SEL6-2681: for debouncing Promises, use awesome-debounce-promise library, together with onlyResolvesLast option to support the react-select's cache feature
    this.loadOptions = debounce(this.loadOptions, SearchScopeSelector.DEBOUNCE_TIMER, { onlyResolvesLast: true });
  }

  static selectOptionFormatter(option: SearchScope, labelMeta: FormatOptionLabelMeta<Template>): ReactElement {
    const isSelection: boolean = labelMeta.context === "value";

    // used for both the option list and the selected option panel
    const valueClass: string = clsx("multi-select-option", {
      selection: isSelection,
    });
    const showSecondaryName: boolean = option.secondaryName && !isSelection;

    return (
      <div key={"select-option-" + option.id} className={valueClass}>
        <span className="main-label">{option.name}</span>{" "}
        {showSecondaryName && <span className="secondary">({option.secondaryName})</span>}
      </div>
    );
  }

  onChangeRestrictionType() {
    // reset the selected search scope options if user switches between genome, database, species
    this.props.change(this.props.scopeFieldKey, null);
    // reset the options list (together with the cache) if user switches the restriction type
    this.searchScopeOptionsCache = {};
    // TODO do not use the Async one and take care of the  loading of options ourselves in order to avoid this kind of workaround !
    this.setState({ searchRestrictionFieldKey: Math.floor(Math.random() * 1000) });
  }

  loadOptions(searchTerm: string) {
    return this.props
      .loadScopeOptions(searchTerm, this.props.restrictionType?.value)
      .then((searchScopes: SearchScope[]) => {
        return searchScopes;
      });
  }

  render(): ReactElement {
    const restrictionTypes: ISelectOption[] = [
      restrictionTypeOptions[SEARCH_RESTRICTION_TYPES.GENOME],
      restrictionTypeOptions[SEARCH_RESTRICTION_TYPES.SPECIES],
    ];

    if (
      this.props.searchType !== ENTITY_TYPE.GENE &&
      this.props.searchType !== ENTITY_TYPE.MARKER &&
      this.props.searchType !== ENTITY_TYPE.NUFEATURE &&
      this.props.searchType !== ENTITY_TYPE.AAFEATURE
    ) {
      restrictionTypes.push(restrictionTypeOptions[SEARCH_RESTRICTION_TYPES.DATABASE]);
    }

    return (
      <FormInputGroup>
        <FormLabel>{"Restrict search to"}</FormLabel>
        <FormInputWidget>
          <HStack>
            <div style={{ width: 120 }}>
              <ReactSelectReduxFormField
                component={ReactSelectField}
                handleChange={this.onChangeRestrictionType}
                name={this.props.restrictionTypeFieldKey}
                isClearable={false}
                options={restrictionTypes}
                className="restrictionTypeElement"
              />
            </div>
            <div style={{ flex: 1 }}>
              <ReactSelectReduxFormField
                name={this.props.scopeFieldKey}
                key={this.state.searchRestrictionFieldKey.toString()}
                placeholder={"Type to search"}
                noOptionsMessage={() => "No result for this input"}
                component={ReactSelectField}
                async={true}
                getOptionValue={(option: SearchScope) => option.id}
                getOptionLabel={(option: SearchScope) => option.name}
                loadOptions={this.loadOptions}
                formatOptionLabel={SearchScopeSelector.selectOptionFormatter}
                isMulti
                isClearable
                isSearchable
                horizontalLayout={false}
                cacheOptions={this.searchScopeOptionsCache}
              />
            </div>
          </HStack>
        </FormInputWidget>
      </FormInputGroup>
    );
  }
}

export { SearchScopeSelector };
export default SearchScopeSelector;
